import type { FilterFn } from "@tanstack/vue-table";
import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { NumberType } from "../../input-number/InputNumber";
import type { FlexComponent } from "@/utils/render-component";
import { debounce } from "lodash-es";
import { RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { computed, defineComponent, ref, watch } from "vue";
import { $t } from "@/services/i18n";
import { p } from "@/utils/props-builder";
import Badge from "../../badge/Badge";
import Button from "../../button/Button";
import FacetedFilter from "../../faceted-filter/FacetedFilter";
import InputNumber from "../../input-number/InputNumber";
import Separator from "../../separator/Separator";
import { useTableEmitter } from "../composables/helpers";
import { injectTable } from "../DataTable";
import { defaultFacetedProps } from "./props";

const operatorOptions = [
  {
    value: "eq",
    label: "=",
    text: () => $t("equal"),
  },
  {
    value: "neq",
    label: "≠",
    text: () => $t("not_equal"),
  },
  {
    value: "lt",
    label: "<",
    text: () => $t("less_than"),
  },
  {
    value: "gt",
    label: ">",
    text: () => $t("greater_than"),
  },
  {
    value: "lte",
    label: "≤",
    text: () => $t("less_or_equal"),
  },
  {
    value: "gte",
    label: "≥",
    text: () => $t("greater_or_equal"),
  },
  {
    value: "between",
    label: "≬",
    text: () => $t("between"),
  },
] as const;

export type NumberOperator = (typeof operatorOptions)[number]["value"];

export type NumberFilter = {
  [K in NumberOperator]: K extends "between"
    ? {
        _type: "number";
        operator: K;
        value: [number | null, number | null];
      }
    : {
        _type: "number";
        operator: K;
        value: number | null;
      };
}[NumberOperator];

export function formatNumberFilter<T extends NumberOperator>(
  operator: T,
  value: T extends "between" ? [number | null, number | null] : number | null
): NumberFilter {
  return {
    _type: "number",
    operator,
    value,
  } as NumberFilter;
}

const numberFilterProps = {
  ...defaultFacetedProps,
  min: p.number(0),
  max: p.number(),
  type: p.string<NumberType>("default"),
  currency: p.string(),
  step: p.number(1),
  options: p.array<NumberOperator[]>(operatorOptions.map((i) => i.value)),
} satisfies ComponentPropsOptions;

export type NumberFacetedFilterProps = ExtractPublicPropTypes<typeof numberFilterProps>;

export default defineComponent({
  name: "NumberFactedFilter",
  props: numberFilterProps,
  setup(props) {
    const table = injectTable();
    const column = table.getColumn(props.accessorKey);
    const operator = ref(props.options[0]);
    const value = ref<number | null>(null);
    const value2 = ref<number | null>(props.max ?? (value.value ? value.value + 1 : null));
    const inputRef = ref<InstanceType<typeof InputNumber> | null>(null);

    function focusOnInput() {
      if (!inputRef.value?.$el) return;
      inputRef.value.$el.querySelector("input")?.focus();
    }

    const columnFilter = computed(() => {
      const filter = column?.getFilterValue();

      if (!isNumberFilter(filter)) return undefined;
      return filter;
    });

    watch(columnFilter, (val) => {
      if (!val) {
        value.value = null;
        return;
      }
    });

    watch(
      [operator, value, value2],
      debounce(([operator, value, value2]) => {
        if (!column) return;
        if (value === null || value === undefined) column.setFilterValue(null);

        if (operator === "between") {
          column.setFilterValue(formatNumberFilter("between", [value, value2]));
          return;
        }

        column.setFilterValue(formatNumberFilter(operator, value));
      }, props.debounce)
    );

    const operators = computed(() => {
      return props.options.map((option) => {
        return {
          value: option,
          label: operatorOptions.find((i) => i.value === option)?.label ?? "",
          text: operatorOptions.find((i) => i.value === option)?.text?.() ?? "",
        };
      });
    });

    const Badges = computed<FlexComponent | undefined>(() => {
      if (value.value === null || value.value === undefined) return undefined;

      const text = `${
        operators.value.find((i) => i.value === operator.value)?.text
      } ${value.value} ${operator.value === "between" ? `${$t("and")} ${value2.value}` : ""}`;

      return (
        <Badge variant="muted" size="xs">
          {text}
        </Badge>
      );
    });

    function selectNextOperator() {
      const index = operators.value.findIndex((i) => i.value === operator.value);

      const nextIndex = index + (1 % operators.value.length);

      if (!operators.value[nextIndex]) {
        operator.value = operators.value[0].value;
        return;
      }

      operator.value = operators.value[nextIndex].value;
    }

    function selectPreviousOperator() {
      const index = operators.value.findIndex((i) => i.value === operator.value);

      const nextIndex = index - 1 < 0 ? operators.value.length - 1 : index - 1;

      if (!operators.value[nextIndex]) return;

      operator.value = operators.value[nextIndex].value;
    }

    function onKeydown(e: KeyboardEvent) {
      if (e.code === "ArrowLeft") {
        e.preventDefault();
        selectPreviousOperator();
      }

      if (e.code === "ArrowRight") {
        e.preventDefault();
        selectNextOperator();
      }
    }

    useTableEmitter("set-filter", (filter) => {
      if (filter.id !== table.id || filter.column !== props.accessorKey) return;

      const newVal = filter.setter({
        operator: operator.value,
        value: operator.value === "between" ? [value.value, value2.value] : value.value,
      });

      if (newVal) {
        operator.value = newVal.operator as NumberOperator;

        if (Array.isArray(newVal.value)) {
          value.value = newVal.value[0];
          value2.value = newVal.value[1];
        } else {
          value.value = newVal.value;
        }
      }
    });

    return () => (
      <FacetedFilter
        label={props.label}
        badges={Badges.value}
        size={props.size}
        active={value.value !== null && value.value !== undefined}
      >
        <div class="max-w-[18rem]">
          <div class="flex gap-2 border-b p-2">
            <InputNumber
              ref={inputRef}
              min={props.min}
              max={props.max}
              v-model={value.value}
              clearable={false}
              type={props.type}
              currency={props.currency}
              step={props.step}
              //@ts-expect-error
              onKeydown={onKeydown}
            />
            {operator.value === "between" && (
              <InputNumber
                min={value.value ? value.value + 1 : props.min}
                max={props.max}
                v-model={value2.value}
                clearable={false}
                type={props.type}
                currency={props.currency}
                step={props.step}
              />
            )}
          </div>
          <div class="flex w-full">
            <RadioGroupRoot v-model={operator.value} class="flex w-full justify-between p-1">
              {operators.value.map((option) => {
                return (
                  <RadioGroupItem
                    value={option.value}
                    class="focus:outline-text-muted-foreground/40 center w-full rounded-sm py-1.5 outline-hidden hover:backdrop-brightness-110 focus:outline-dashed focus:outline-1 focus:-outline-offset-1 focus:backdrop-brightness-95 data-[state=checked]:bg-muted"
                  >
                    {option.label}
                  </RadioGroupItem>
                );
              })}
            </RadioGroupRoot>
          </div>
          {(value.value !== null && value.value !== undefined) && (
            <>
              <Separator class="bg-muted" />
              <div class="p-1">
                <Button
                  block
                  variant="subtle"
                  label={$t("clear_filter")}
                  onClick={() => {
                    if (!columnFilter.value) return;
                    value.value = null;
                    focusOnInput();
                  }}
                ></Button>
              </div>
            </>
          )}
        </div>
      </FacetedFilter>
    );
  },
});

export function isNumberFilter(filter: unknown): filter is NumberFilter {
  if (typeof filter !== "object") return false;
  if (!filter) return false;

  return "_type" in filter && (filter as any)._type === "number";
}

export const numberFilter: FilterFn<any> = (row, id, comparator: NumberFilter) => {
  if (comparator.value === null || comparator.value === undefined) return true;
  const value = row.getValue(id) as number;

  if (comparator.operator === "eq") return value === comparator.value;
  if (comparator.operator === "neq") return value !== comparator.value;
  if (comparator.operator === "gt") return value > comparator.value;
  if (comparator.operator === "lt") return value < comparator.value;
  if (comparator.operator === "gte") return value >= comparator.value;
  if (comparator.operator === "lte") return value <= comparator.value;
  if (comparator.operator === "between") {
    const [min, max] = comparator.value;
    return value >= (min ?? 0) && value <= (max ?? Infinity);
  }

  return true;
};
